import { isDefined, isObject, isString } from '@aloft/toolkit-shared';
import { Typography, TypographyChildren } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { ListItem, OrderedList, UnorderedList, chakra } from '@aloft/toolkit-sui/chakra';
import { Options, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  Block,
  INLINES,
  Inline,
  MARKS,
  Document as TContentfulDocument,
} from '@contentful/rich-text-types';

import { Link } from 'react-router-dom';

type Props = {
  document: TContentfulDocument;
};

export const ContentfulDocument = ({ document }: Props) => {
  if (!isContentfulDocument(document)) {
    console.error('Invalid Contentful document: ', document);
    return null;
  }

  return documentToReactComponents(document, renderOptions);
};

const hasDataUri = (node: Inline): boolean => isDefined(node.data.uri) && isString(node.data.uri);

const isImageLink = (node: Inline): boolean =>
  (node.data.uri as string).match(/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i) !== null &&
  node.content[0]?.nodeType === 'text' &&
  (node.content[0]?.value as string).match(/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i) !== null;

const isYouTubeEmbedLink = (node: Inline): boolean =>
  (node.data.uri as string).includes('https://www.youtube.com/embed') &&
  node.content[0]?.nodeType === 'text' &&
  node.content[0]?.value.includes('https://www.youtube.com/embed');

const isContentfulDocument = (x: unknown): x is TContentfulDocument =>
  isObject(x) &&
  'nodeType' in x &&
  x.nodeType === BLOCKS.DOCUMENT &&
  'content' in x &&
  Array.isArray(x.content);

const Hyperlink = ({ node, children }: { node: Inline | Block; children: React.ReactNode }) => {
  if (node.nodeType !== INLINES.HYPERLINK || !hasDataUri(node)) return null;

  // Lets us embed images in Contentful documents by adding a hyperlink whose
  // href and link text are both an image URL (e.g. https://example.com/photo.png)
  if (isImageLink(node)) {
    return (
      <chakra.img
        src={node.data.uri as string}
        alt="Image from Contentful"
        maxWidth="100%"
        style={{ display: 'block', margin: '8px 0' }}
      />
    );
  }

  // Lets us "embed" YouTube videos in Contentful documents by adding
  // embed links in the form of `https://www.youtube.com/embed/<ID>`
  if (isYouTubeEmbedLink(node)) {
    return (
      <iframe
        width="100%"
        height="220px"
        src={node.data.uri as string}
        title="YouTube video player"
        allow="fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        style={{ border: '1px solid #ddd' }}
      />
    );
  }

  return (
    <Link to={node.data.uri as string} target="_blank" rel="noopener noreferrer">
      <Typography
        variant="link"
        inline // don't let a <p> get nested inside of a <p>
      >
        {children as TypographyChildren}
      </Typography>
    </Link>
  );
};

const renderOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <chakra.span fontWeight="bold">{text}</chakra.span>,
    [MARKS.ITALIC]: (text) => <chakra.span fontStyle="italic">{text}</chakra.span>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Typography>{children as TypographyChildren}</Typography>,
    [BLOCKS.HEADING_1]: (_, children) => (
      <Typography variant="h1">{children as TypographyChildren}</Typography>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Typography variant="h2">{children as TypographyChildren}</Typography>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Typography variant="h3">{children as TypographyChildren}</Typography>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <Typography variant="h4">{children as TypographyChildren}</Typography>
    ),
    [BLOCKS.HEADING_5]: (_, children) => (
      <Typography variant="h5">{children as TypographyChildren}</Typography>
    ),
    [BLOCKS.UL_LIST]: (_, children) => <UnorderedList>{children}</UnorderedList>,
    [BLOCKS.OL_LIST]: (_, children) => <OrderedList>{children}</OrderedList>,
    [BLOCKS.LIST_ITEM]: (_, children) => <ListItem>{children}</ListItem>,
    [INLINES.HYPERLINK]: (node, children) => <Hyperlink node={node}>{children}</Hyperlink>,
  },
};
